import { useMemo } from 'react';
import { useQueryClient, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import {
  QueryKeys,
  ParticipantDto,
  DomainDto,
  ApplicationDto,
  RemoteParticipantDto,
  RemoteDomainDto,
  ValidatorResponseDto,
  ResourcesDto,
} from 'data';

import { prepareEnvVarsToForm, prepareResourcesToForm, prepareStorageToForm } from '../helpers';

export enum ListKey {
  DOMAINS_KEY = 'domains',
  PARTICIPANTS_KEY = 'participants',
  APPLICATIONS_KEY = 'applications',
}

export const useInitialEntityValues = <
  T extends Pick<
    ParticipantDto | DomainDto | ApplicationDto | RemoteParticipantDto | RemoteDomainDto | ValidatorResponseDto,
    'name' | 'envVars'
  > & {
    resources?: ResourcesDto;
  } & Partial<Pick<ApplicationDto, 'port'>> &
    Partial<Pick<ParticipantDto | DomainDto, 'storage'>>,
  F,
>(
  queryKey: QueryKeys,
  entityName: string,
  fn: (v: string, options?: UseQueryOptions<T, any, F>) => UseQueryResult<T, unknown>,
  listKey?: ListKey,
) => {
  const queryClient = useQueryClient();
  let entity = queryClient.getQueryData<T>([queryKey, entityName]);

  if (!entity) {
    const list = queryClient.getQueryData<T[]>([queryKey]);
    entity = listKey ? list?.[listKey]?.find((p) => p.name === entityName) : list?.find((p) => p.name === entityName);
  }

  const { data: fetchedEntity } = fn(entityName, { enabled: true }); // !entity

  const initialValues = useMemo(() => {
    // should be changed later (should not contain all values/pairs, but only changeable)
    if (entity) {
      const { resources, envVars, storage, port } = entity;
      const preparedResources = prepareResourcesToForm(resources);
      const envVarsForForm = prepareEnvVarsToForm(envVars);
      const preparedStorage = prepareStorageToForm(storage);

      return {
        ...entity,
        resources: preparedResources,
        envVars: envVarsForForm?.preparedEnvVars,
        postgresEnvVars: envVarsForForm?.postgresVars,
        storage: preparedStorage,
        port: listKey === ListKey.APPLICATIONS_KEY ? port : null,
      } as F;
    }

    if (fetchedEntity) {
      const { resources, envVars, storage, port } = fetchedEntity;
      const preparedResources = prepareResourcesToForm(resources);
      const envVarsForForm = prepareEnvVarsToForm(envVars);
      const preparedStorage = prepareStorageToForm(storage);

      return {
        ...fetchedEntity,
        resources: preparedResources,
        envVars: envVarsForForm?.preparedEnvVars,
        postgresEnvVars: envVarsForForm?.postgresVars,
        storage: preparedStorage,
        port: listKey === ListKey.APPLICATIONS_KEY ? port : null,
      } as F;
    }

    return {} as F;
  }, [entity, fetchedEntity]);

  return { initialValues, entity };
};
